import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import Toggle from 'react-toggle';
import OkCancelModal from '@/shared/components/OkCancelModal';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import ToastrService from '@/shared/services/ToastrService';
import DocumentService from '@/shared/services/DocumentService';
import enumCache from '@/shared/services/EnumCacheService';
import { useGetDocumentTypeSettings, } from '@/shared/hooks/settings/useGetDocumentTypeSettings';
import { useUpdateDocumentTypeSetting } from '@/shared/hooks/settings/useUpdateDocumentTypeSetting';
const messages = defineMessages({
    SigningRequiredModalTitle: {
        id: 'SigningRequiredModalTitle',
        defaultMessage: 'Signing required per document type',
    },
    SigningRequiredAppliesToWholeTenant: {
        id: 'SigningRequiredAppliesToWholeTenant',
        defaultMessage: 'This setting applies to your whole organization, not just the selected company.',
    },
    SigningRequiredInfo: {
        id: 'SigningRequiredInfo',
        defaultMessage: "This change is not retroactive: it only applies to documents added from now on. Existing documents keep their current signing status. Use the 'Change signing status' global action on the documents screen to update those.",
    },
    SigningRequiredSettingsSaved: {
        id: 'SigningRequiredSettingsSaved',
        defaultMessage: 'The signing settings have been saved.',
    },
    Save: {
        id: 'Save',
        defaultMessage: 'Save',
    },
});
// A missing row, or a row with no stored value, means signing is still required.
const isSigningRequired = (settings, documentType) => { var _a, _b; return (_b = (_a = settings.find(x => x.documentType === documentType)) === null || _a === void 0 ? void 0 : _a.setSigningRequired) !== null && _b !== void 0 ? _b : true; };
const SigningRequiredForm = ({ show, onClose, documentTypes, settings, }) => {
    const intl = useIntl();
    const { updateDocumentTypeSetting, isLoading: saving } = useUpdateDocumentTypeSetting();
    const initialRows = useMemo(() => documentTypes.map(documentType => (Object.assign(Object.assign({}, documentType), { signingRequired: isSigningRequired(settings, documentType.key) }))), [documentTypes, settings]);
    const [rows, setRows] = useState(initialRows);
    const isDirty = rows.some(row => { var _a; return row.signingRequired !== ((_a = initialRows.find(x => x.key === row.key)) === null || _a === void 0 ? void 0 : _a.signingRequired); });
    const handleToggle = (key, value) => {
        setRows(prev => prev.map(row => (row.key === key ? Object.assign(Object.assign({}, row), { signingRequired: value }) : row)));
    };
    const handleSave = async () => {
        if (!isDirty)
            return;
        const changedRows = rows.filter(row => { var _a; return row.signingRequired !== ((_a = initialRows.find(x => x.key === row.key)) === null || _a === void 0 ? void 0 : _a.signingRequired); });
        try {
            await Promise.all(changedRows.map(row => updateDocumentTypeSetting({
                DocumentType: row.key,
                SetSigningRequired: row.signingRequired,
            })));
            ToastrService.success(intl.formatMessage(messages.SigningRequiredSettingsSaved));
            onClose();
        }
        catch (_a) {
            // error toastr already shown by useUpdateDocumentTypeSetting hook
        }
    };
    return (_jsxs(OkCancelModal, Object.assign({ title: intl.formatMessage(messages.SigningRequiredModalTitle), show: show, onClose: onClose, onSave: handleSave, disabled: saving || !isDirty, okLabel: intl.formatMessage(messages.Save), disableFullScreen: true }, { children: [_jsx("p", Object.assign({ className: "text-muted" }, { children: intl.formatMessage(messages.SigningRequiredAppliesToWholeTenant) })), _jsx("div", Object.assign({ className: "form-horizontal", role: "form" }, { children: _jsx("div", Object.assign({ className: "form-group" }, { children: _jsx("div", Object.assign({ className: "col-sm-12" }, { children: rows.map(row => (_jsx("div", { children: _jsxs("label", Object.assign({ className: "react-toggle-wrapper margin5T" }, { children: [_jsx(Toggle, { checked: row.signingRequired, onChange: e => handleToggle(row.key, e.target.checked) }), _jsx("span", Object.assign({ className: "label-text" }, { children: row.label }))] })) }, row.key))) })) })) })), _jsx("div", Object.assign({ className: "alert alert-info" }, { children: intl.formatMessage(messages.SigningRequiredInfo) }))] })));
};
// The settings and the list of configurable document types are only fetched when the modal opens.
const SigningRequiredModal = ({ show, onClose }) => {
    const intl = useIntl();
    const { data: settings, isLoading } = useGetDocumentTypeSettings(show);
    const [documentTypes, setDocumentTypes] = useState(null);
    useEffect(() => {
        if (!show)
            return undefined;
        let cancelled = false;
        Promise.all([
            DocumentService.getSigningRequiredConfigurableDocumentTypes(),
            enumCache.get('documenttype'),
        ]).then(([configurableKeys, allDocumentTypes]) => {
            if (cancelled)
                return;
            setDocumentTypes((configurableKeys || []).map(key => {
                const documentType = (allDocumentTypes || []).find(x => x.key === key);
                return {
                    key,
                    label: intl.formatMessage(listItemMsg('documenttype', key, documentType === null || documentType === void 0 ? void 0 : documentType.value)),
                };
            }));
        });
        return () => {
            cancelled = true;
        };
    }, [show, intl]);
    if (isLoading || !settings || !documentTypes) {
        return (_jsx(OkCancelModal, Object.assign({ title: intl.formatMessage(messages.SigningRequiredModalTitle), show: show, onClose: onClose, onSave: () => undefined, disabled: true, okLabel: intl.formatMessage(messages.Save), disableFullScreen: true }, { children: _jsx(LoadingContainer, { isLoading: true }) })));
    }
    return (_jsx(SigningRequiredForm, { show: show, onClose: onClose, documentTypes: documentTypes, settings: settings }));
};
export default SigningRequiredModal;
